import { action, flow, makeObservable, observable } from 'mobx';
import { FormStore } from '@roc/feature-app-core';
import { format, parseISO, isValid } from 'date-fns';
import { GlobalStore } from '../../../stores';
import { CompanyProfileService } from '../services/CompanyProfileService';
import { ExternalLenderStore } from './externalLenderStore';
import { LicenseGridStore } from './licenseGridStore';

const formatDate = (value: any): string | null => {
  if (!value) return null;
  try {
    const date = typeof value === 'string' ? parseISO(value) : new Date(value);
    return isValid(date) ? format(date, 'MM/dd/yyyy') : null;
  } catch {
    return null;
  }
};

const form = {
  fields: {
    type: {
      value: '',
      error: null,
      rule: 'required',
    },
    originator: {
      value: null,
      error: null,
      rule: '',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
    },
    expirationDate: {
      value: null,
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export interface LenderLicense {
  lenderLicenseId?: number;
  lenderId?: number;
  lenderDocumentId?: number;
  type: string;
  originator?: number;
  originatorFullName?: string;
  state?: string;
  expirationDate?: string;
}

export class LicenseDetailsStore extends FormStore {
  private globalStore: GlobalStore;
  salesforceLenderStore: ExternalLenderStore;
  companyProfileService: CompanyProfileService;

  gridStore: LicenseGridStore;
  isDialogOpen = false;
  editingLicense: LenderLicense | null = null;
  originatorOptions: { label: string; value: string }[] = [];
  pendingFile: File | null = null;
  documentPreview: { title: string; data: ArrayBuffer; headers: object } | null = null;

  constructor(globalStore: GlobalStore, salesforceLenderStore: ExternalLenderStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.salesforceLenderStore = salesforceLenderStore;
    this.companyProfileService = new CompanyProfileService();
    this.gridStore = new LicenseGridStore(globalStore, salesforceLenderStore);

    makeObservable(this, {
      gridStore: observable.ref,
      isDialogOpen: observable,
      editingLicense: observable,
      originatorOptions: observable,
      pendingFile: observable,
      documentPreview: observable,
      openAddDialog: action,
      openEditDialog: action,
      closeDialog: action,
      closeDocumentPreview: action,
      uploadLicenseDocument: action,
      saveLicense: flow,
      previewLicenseDocument: flow,
      loadOriginators: flow,
    });
  }

  openAddDialog() {
    this.editingLicense = null;
    this.pendingFile = null;
    this.reset();
    this.isDialogOpen = true;
    this.loadOriginators();
  }

  openEditDialog(license: LenderLicense) {
    if (!license) return;
    this.editingLicense = license;
    this.pendingFile = null;
    this.loadForm({
      type: license.type,
      originator: license.originator ?? null,
      state: license.state ?? '',
      expirationDate: license.expirationDate ?? null,
    });
    this.isDialogOpen = true;
    this.loadOriginators();
  }

  closeDialog() {
    this.isDialogOpen = false;
    this.editingLicense = null;
    this.pendingFile = null;
    this.reset();
  }

  uploadLicenseDocument(file: File) {
    this.pendingFile = file;
  }

  *loadOriginators() {
    try {
      const companyId = this.salesforceLenderStore.salesForceLender?.companyId;
      if (!companyId) return;
      const response = yield this.companyProfileService.getCompanyContacts(companyId);
      const rows = response?.data.data ?? [];
      this.originatorOptions = rows
      .filter(u => !u.deactivated)
      .map(u => ({ label: u?.contact?.fullName, value: u?.userId }));
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error loading originators.',
      });
    }
  }

  *saveLicense() {
    try {
      const isEditMode = !!this.editingLicense?.lenderLicenseId;

      if (!isEditMode) {
        this.form.fields.originator.rule =
          this.form.fields.type.value === 'ORIGINATOR' ? 'required' : '';
        this.runFormValidation();
        if (!this.form.meta.isValid) return;
      } else {
        // In edit mode only expiration date is editable — validate it in isolation
        if (!this.form.fields.expirationDate.value) {
          this.form.fields.expirationDate.error = 'Expiration date is required.';
          return;
        }
        this.form.fields.expirationDate.error = null;
      }

      if (!isEditMode && !this.pendingFile) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Please upload a license document before saving.',
        });
        return;
      }

      const values = this.getFormValues();
      const lenderId = this.salesforceLenderStore.salesForceLender?.lenderId;

      const type = isEditMode ? this.editingLicense.type : values.type;
      const state = isEditMode ? this.editingLicense.state : values.state;
      const originator = isEditMode
        ? this.editingLicense.originator
        : (type === 'ORIGINATOR' ? (values.originator || null) : null);

      const license: LenderLicense = {
        ...(this.editingLicense ?? {}),
        lenderId,
        type,
        originator,
        state,
        expirationDate: formatDate(values.expirationDate),
      };

      const formData = new FormData();
      formData.append('license', new Blob([JSON.stringify(license)], { type: 'application/json' }));
      if (this.pendingFile) {
        formData.append('file', this.pendingFile);
        formData.append('fileType', `${type}_LICENSE`);
      }

      yield this.companyProfileService.saveLenderLicense(formData);
      this.gridStore.fetchGridData();
      this.closeDialog();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'License saved successfully.',
      });
    } catch (e: any) {
      const message = e?.error?.response?.data?.error?.message
        || e?.response?.data?.error?.message
        || 'Error saving license.';
      this.globalStore.notificationStore.showErrorNotification({ message });
    }
  }

  closeDocumentPreview() {
    this.documentPreview = null;
  }

  *previewLicenseDocument(lenderDocumentId: number, title: string) {
    try {
      const response = yield this.companyProfileService.downloadLenderDocument(lenderDocumentId);
      this.documentPreview = {
        title,
        data: response.data,
        headers: response.headers,
      };
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error loading document preview.',
      });
    }
  }
}
